import React from 'react'
import { useTable, useSortBy } from 'react-table'
import { CSVLink } from 'react-csv'
import { RichText, Palette, Link } from '@uswitch/ev-core/react'

import sw from '@uswitch/ev-utils/switch'
import { noop } from '../../../utils/utils'

import { Table, Thead, Tbody, Th, Tr, Td, Button, Type } from '@uswitch/spark'
import { SortButton, SortButtonVariants } from './sort-button'
import { Types, annotateTableContents } from './utils'
import withComponentBase from '../../helpers/with-component-base'

const Wrapper = ({ C, children, wrap = false }) => (
  <Choose>
    <When condition={wrap}>
      <C>{children}</C>
    </When>
    <Otherwise>{children}</Otherwise>
  </Choose>
)
const link = href => ({ children }) => (
  <Link href={href} target='_blank' rel='noreferrer'>
    {children}
  </Link>
)

const MarksWrapper = ({ marks: { link: href, ...marks } = {}, children }) => {
  return (
    <span style={markStyles(marks)}>
      <Wrapper C={link(href)} wrap={href}>
        {children}
      </Wrapper>
    </span>
  )
}

const PaletteTypes = {
  BACKGROUND: 'backgroundColor',
  FEATURED_TEXT: 'featureTextColor',
  TEXT: 'textColor',
}

/**
 * Create styles objects used when table is on a background color
 * This removes BG and uses borders to distinguish rows
 * @param {Number} rowIndex - Row Index, 0th row has a 3px border
 */
const rowPx = rowIndex => ({
  color: p => (p[PaletteTypes.BACKGROUND] ? p[PaletteTypes.TEXT] || 'black' : ''),
  backgroundColor: p => (p[PaletteTypes.BACKGROUND] ? 'transparent !important' : ''),
  borderBottom: p =>
    p.backgroundColor ? `${p[PaletteTypes.TEXT] || 'black'} ${rowIndex === 0 ? 3 : 1}px solid` : '',
})

const markStyles = (marks = {}) => ({
  fontWeight: marks.b ? '600' : 'initial',
  fontStyle: marks.i ? 'italic' : 'initial',
  textDecoration: marks.u ? 'underline' : 'initial',
})

const TDate = ({ date, children }) => {
  if (!date) return null

  const dateObj = new Date(date)
  const dateString = [
    dateObj.getDate(),
    dateObj.toLocaleString('default', { month: 'long' }),
    dateObj.getFullYear(),
  ].join(' ') // Format to DD mmm YYYY, e.g. 19 June 2021

  return (
    <React.Fragment>
      {children} {dateString}
    </React.Fragment>
  )
}

const TableCompound = ({
  title,
  contents,
  footnotes,
  localDates,
  globalDates,
  displayDate = true,
  variant,
  exportable = false,
  sortable = false,
  ...rest
}) => {
  const linkRef = React.useRef()
  const publishDateString = localDates?.dateCreated || globalDates?.dateCreated
  const updatedDateString = localDates?.dateUpdated || globalDates?.dateUpdated

  const publishDate = displayDate
    ? publishDateString
      ? new Date(publishDateString)
      : null
    : undefined
  const updatedDate = displayDate
    ? updatedDateString
      ? new Date(updatedDateString)
      : null
    : undefined

  const contentWithNoEmptyRows = React.useMemo(
    () => contents.filter(row => row.some(cell => !!cell.value)),
    [contents],
  )

  const { isSimpleTable, annotatedContents, columns, data, csvData } = React.useMemo(() => {
    /**
     * Determines whether a table is a standard thead + tbody and can
     * therefore be sorted. This will return false if there are either
     * `th` cells in rows >1 or if there are none `th` cells in the
     * first row
     */
    const isSimpleTable =
      sortable &&
      !contentWithNoEmptyRows.slice(1).some(row => row.some(cell => cell.type === Types.TH)) &&
      contentWithNoEmptyRows[0].every(cell => cell.type === Types.TH)

    /**
     * Columns structure
     * [ { Header: 'foo', accessor: 'col0' }, { Header: 'bar', accessor: 'col1' }]
     * [
     *    { col0: 'foo value', col1: 'bar value' } // Row 1
     *    { col0: 'foo value', col1: 'bar value' } // Row 2
     * ]
     */
    const columns = contentWithNoEmptyRows[0].map((it, cellId) => ({
      Header: it.value,
      accessor: `col${cellId}`,
    }))

    const rowContents = isSimpleTable ? contentWithNoEmptyRows.slice(1) : contentWithNoEmptyRows
    const data = rowContents.map(row =>
      row.reduce(
        (acc, cell, cellId) => ({
          ...acc,
          [`col${cellId}`]: cell.value,
        }),
        {},
      ),
    )

    const csvData = data.map(row => Object.values(row))
    const annotatedContents = annotateTableContents(contentWithNoEmptyRows)

    return { columns, data, csvData, isSimpleTable, annotatedContents }
  }, [contentWithNoEmptyRows, sortable])

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    isSimpleTable && useSortBy,
  )

  const tableStyles = !footnotes
    ? {}
    : {
        borderBottomStyle: 'solid',
        borderBottomWidth: '1px',
        borderBottomColor: 'grey-20',
      }

  return (
    <div
      sx={{
        marginBottom: '16px',
      }}
      {...rest}
    >
      <If condition={title}>
        <Palette as={Type.H3} px={{ color: PaletteTypes.FEATURED_TEXT }}>
          {title}
        </Palette>
      </If>

      <If condition={updatedDate || publishDate}>
        <Palette
          sx={{ marginBottom: '16px' }}
          px={{ color: PaletteTypes.TEXT }}
          as={({ children, ...rest }) => (
            <Type.S2 as='div' {...rest}>
              {children}
            </Type.S2>
          )}
        >
          <Choose>
            <When condition={updatedDate}>
              <TDate date={updatedDate}>Updated</TDate>
            </When>
            <When condition={publishDate}>
              <TDate date={publishDate}>Published</TDate>
            </When>
          </Choose>
        </Palette>
      </If>

      <div
        sx={{
          overflowX: 'auto',
          marginBottom: '16px',
        }}
      >
        <Palette
          as={Table}
          sx={tableStyles}
          px={{ border: p => (p[PaletteTypes.BACKGROUND] ? 'none' : '') }}
          {...getTableProps()}
        >
          <If condition={isSimpleTable}>
            <Thead>
              {headerGroups.map(headerGroup => (
                <Palette as={Tr} px={rowPx(0)} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, c) => {
                    const { marks, type, id, headers } = annotatedContents[0][c] // Get the marks for the current column
                    const T = type === Types.TH ? Th : Td

                    const getSortByToggleProps =
                      typeof column.getSortByToggleProps === 'function'
                        ? column.getSortByToggleProps
                        : noop

                    return (
                      <Palette
                        as={T}
                        id={id}
                        headers={headers}
                        style={{ cursor: isSimpleTable ? 'pointer' : 'initial' }}
                        px={rowPx()}
                        {...column.getHeaderProps(getSortByToggleProps())}
                      >
                        <MarksWrapper marks={marks}>{column.render('Header')}</MarksWrapper>

                        <If condition={isSimpleTable && column.canSort}>
                          <SortButton
                            variant={sw({
                              [column.isSortedDesc]: SortButtonVariants.DESCENDING,
                              [column.isSortedDesc === false]: SortButtonVariants.ASCENDING,
                              default: SortButtonVariants.INACTIVE,
                            })(true)}
                          />
                        </If>
                      </Palette>
                    )
                  })}
                </Palette>
              ))}
            </Thead>
          </If>

          <Tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row)

              return (
                <Palette
                  as={Tr}
                  {...row.getRowProps()}
                  px={rowPx(isSimpleTable ? row.index + 1 : row.index)}
                  striped
                >
                  {row.cells.map((cell, c) => {
                    // Use the row index +1 when sortable as this is
                    // modified by sorting and doesnt take into
                    // account the headings
                    const index = isSimpleTable ? cell.row.index + 1 : cell.row.index

                    const { marks, type, id, headers } = annotatedContents[index][c] || {}
                    const T = type === Types.TH ? Th : Td

                    return (
                      <Palette
                        as={T}
                        id={id}
                        headers={headers}
                        px={rowPx()}
                        sx={markStyles(marks)}
                        {...cell.getCellProps()}
                      >
                        <MarksWrapper marks={marks}>{cell.render('Cell')}</MarksWrapper>
                      </Palette>
                    )
                  })}
                </Palette>
              )
            })}
          </Tbody>
        </Palette>
      </div>

      <If condition={footnotes}>
        <Palette as='span' px={{ color: 'textColor' }}>
          <RichText content={footnotes} />
        </Palette>
      </If>

      <If condition={exportable}>
        <Button
          variant={'tertiary'}
          onClick={() => {
            const clickLink = linkRef.current?.link
            if (clickLink) clickLink.click()
          }}
        >
          Export table as CSV
        </Button>
        <CSVLink ref={linkRef} sx={{ display: 'none' }} data={csvData}>
          Export table as CSV
        </CSVLink>
      </If>
    </div>
  )
}

export default withComponentBase(TableCompound, {
  withHeaderFooter: true,
  headerStyles: { pt: 0, pb: 16 },
})
